import axios from "axios";
import { proxyUrl } from "../config";
import { setupInterceptorsTo } from "./Interceptors";
import { DataAndCartLines } from "./PortfolioModelWalletService";

setupInterceptorsTo(axios);

export enum CompetitionTypes {
    "Un ganador",
    "Tres ganadores",
    "Varios ganadores"
}

export type CompetitionInitialValues = ICompetitionInitialValues;

interface ICompetitionInitialValues {
  userId: number;
  savingsPlanId: number;
  name: string;
  startDate: string;
  endDate: string;
  description: string;
  type: number;
  typeOfPrice: number;
  price: number;
  modelPortfolio: string;
  allowOutdatedIncriptions: boolean;
}


interface ICompetition {
    id: number,
    name: string,
    description: string,
    type: CompetitionTypes,
    price: number,
    startDate: string,
    endDate: string,
    competitionsUsers: CompetitionsUsersType[],
    createdAt: string,    
    typeOfPrice: number,
    firstPrice?: string,
    secondPrice?: string,
    thirdPrice?: string,
    specialPrice?:string,
    isPromocionated?: boolean,
    summary?: string,
    urlPayment?: string,
    asociatedContractId?: number,
    ratioId?: number,
    ratioName?: string,
	allowOutdatedIncriptions: boolean;
}
export type CompetitionType = ICompetition;

interface ICompetitions {
    id: number,
    name: string,
    description: string,
    type: CompetitionTypes,
    price: number,
    startDate: string,
    endDate: string,
    totalAmount: number,
    totalAmountPromotionated: number,
    totalParticipants: number, 
    totalParticipantsPromotionated: number,
    createdAt: string,    
    typeOfPrice: number,
    firstPrice?: string,
    secondPrice?: string,
    thirdPrice?: string,
    specialPrice?:string,
    isPromocionated?: boolean,
    summary?: string,
    urlPayment?: string,
    asociatedContractId?: number,
	idUsers: string[],
	allowOutdatedIncriptions: boolean;
}
export type CompetitionsType = ICompetitions;

interface ICompetitionsUsers {
    id: number,
    competitionId: number,
    savingsPlanId: number,
    portfolioModelId: number,
    portfolioModel: PortfolioModelType,
    performance?: number,
    createdAt: string,
    updatedAt: string,
    statusUserInCompetition: number,
    ratio?: number,
	userId?: number

}
export type CompetitionsUsersType = ICompetitionsUsers;

interface ICompetitionsUsersElement {
    competitionId: number,
    savingsPlanId: number,
    portfolioModelId: number,
    userId?: number,
}

interface ICompetitionsUsersWithNames {
    competitionId: number,
    savingsPlanId: number,
    savingPlanName: string,
    portfolioModelName?: string,
    portfolioModelId: number,
    userId?: number,
    competition?: ICompetition
}
export type CompetitionsUsersTypeElement = ICompetitionsUsersElement;
export type CompetitionsUsersWithNames = ICompetitionsUsersWithNames;

interface IValueVirtualPortfolios {
    id: number,
    portfolioid: number,
    dateValue: string,
    dateCreated: string,
    ammount: number,
    performance1m: number,
    performance3m: number,
    performance6m: number,
    performance1y: number,
    performanceYTD: number,
    performance: number
}

export type ValueVirtualPortfoliosType = IValueVirtualPortfolios;

interface IPortfolioModel {
    id: number,
    name: string,
    deleted: boolean,
    savingsPlanId: number,
    jsonFundsData: DataAndCartLines[],
    valueVirtualPortfolios: ValueVirtualPortfoliosType[],
    createdAt: string,
    updatedAt: string
}

export type PortfolioModelType = IPortfolioModel;

export type QuickLink = {
    imgBanner: string;
    url: string;
};

export type ListOfQuickLinks = {
    quicklinks: QuickLink[];
};

export type UserDataCompetition = {
    userId: number;
    firstName: string;
    lastName: string;
    secondLastName?: string;
    email: string;
    mobile: string;
    idCompetition: number;
    idSavingPlan: number;
    idModelPortfolio: number;
    birthday: Date;
    countryCode: string;
};


class CompetitionsService {
    public getCompetitions = async (): Promise<ICompetitions[]> => {
        let url: string = `${proxyUrl}/competitions`;
        const req = await axios.get(url);
        return req.data;
    }

    public getCompetitionsPromotionated = async (): Promise<ICompetitions[]> => {
        let url: string = `${proxyUrl}/competitions?promotionated=true`;
        const req = await axios.get(url);
        return req.data;
    }

    public getCompetition = async (competitionId: number): Promise<ICompetition> => {
        let url: string = `${proxyUrl}/competitions/${competitionId}`;
        const req = await axios.get(url);
        return req.data;
    }

    public getCompetitionBanners = async (competitionId: number): Promise<ListOfQuickLinks> => {
        let url: string = `${proxyUrl}/competition-with-banner/${competitionId}`;
        const req = await axios.get(url);
        return req.data;
    }

    public createCompetition = async (competitionData: CompetitionInitialValues): Promise<ICompetition> => {
        let url: string = `${proxyUrl}/competitions`;
        const req = await axios.post(url, competitionData);
        return req.data;
    }

    public inscribeInCompetition = async (competitionData: CompetitionsUsersTypeElement): Promise<CompetitionsUsersType> => {
        let url: string = `${proxyUrl}/competition/inscription`;
        const req = await axios.post(url, competitionData);
        return req.data;
    }

    public inscribeInCompetitionStatusCancel = async (userId: number): Promise<any> => {
        let url: string = `${proxyUrl}/competition/inscription?competitionUserId=${userId}`;
        const req = await axios.patch(url, null);
        return req.data;
    }

    public inscribeInCompetitionWithElementsSelected = async (competitionData: CompetitionsUsersTypeElement): Promise<CompetitionsUsersType> => {
        let url: string = `${proxyUrl}/competition/inscription?wantElements=true`;
        const req = await axios.post(url, competitionData);
        return req.data;
    }


    public patchUserData = async (competitionData: UserDataCompetition): Promise<any> => {
        let url: string = `${proxyUrl}/competitions/userData`;
        const req = await axios.patch(url, competitionData);
        return req.data;
    }

    public getUserData = async (userId: number): Promise<any> => {
        let url: string = `${proxyUrl}/competitions/userData?idUser=${userId}`;
        const req = await axios.get(url);
        return req.data;
    }

    public patchSignature = async (idCompetition: number): Promise<any> => {
        let url: string = `${proxyUrl}/competition/signature/${idCompetition}`;
        const req = await axios.patch(url, null);
        return req.data;
    }

    public patchSignatureActivePlan = async (idCompetition: number, wantElements: boolean): Promise<any> => {
        let url: string = `${proxyUrl}/competition/signature/people-with-plan/${idCompetition}?wantElements=${wantElements}`;
        const req = await axios.patch(url, null);
        return req.data;
    }

    public patchSignatureWithEmail = async (idCompetition: number, email: string): Promise<any> => {
        let url: string = `${proxyUrl}/competition/signature/${idCompetition}?email=${email}`;
        const req = await axios.patch(url, null);
        return req.data;
    }

    public patchSignatureWithEmailActivePlan = async (idCompetition: number, email: string): Promise<any> => {
        let url: string = `${proxyUrl}/competition/signature/people-with-plan/${idCompetition}?email=${email}`;
        const req = await axios.patch(url, null);
        return req.data;
    }

}

const competitions = new CompetitionsService();
export default competitions;